import { useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { Plus, Trash2 } from 'lucide-react';
import { createTemplate, deleteTemplate, updateTemplate } from '@mkcore/support/schema';
import { Badge, Button, Card, EmptyState, ErrorState, Field, Input, PageHeader, Skeleton } from '@mkcore/ui';
import { ConfirmButton } from '@/components/confirm';
import { MarkdownEditor } from '@/components/markdown-editor';
import { useActionMutation, useErrorMessage } from '@/lib/mutation';
import { useCan } from '@/lib/session';
import { AttachmentsPanel } from '@/features/files/attachments-panel';
import { templatesQuery, type ReplyTemplate } from '@/features/support/templates';

const search = z.object({ id: z.string().uuid().optional() });

export const Route = createFileRoute('/_app/support/templates')({ validateSearch: search, component: TemplatesPage });

/** The editor of one template: name, folder, text with placeholders, and its files. */
function TemplateEditor({ template, placeholders, canWrite }: { template: ReplyTemplate; placeholders: string[]; canWrite: boolean }) {
  const { t } = useTranslation();
  const message = useErrorMessage();
  const navigate = useNavigate({ from: Route.fullPath });
  const [name, setName] = useState(template.name);
  const [folder, setFolder] = useState(template.folder ?? '');
  const [body, setBody] = useState(template.body);
  const [error, setError] = useState<string | null>(null);
  const save = useActionMutation(updateTemplate, { invalidate: [['support', 'template']], silentErrors: true, success: t('common.saved') });
  const remove = useActionMutation(deleteTemplate, { invalidate: [['support', 'template']], success: t('replyTemplates.deleted'), onSuccess: () => void navigate({ search: {}, replace: true }) });
  return (
    <div className="grid content-start gap-4">
      <Card className="grid gap-3 p-4">
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label={t('replyTemplates.name')}>{(p) => <Input {...p} value={name} disabled={!canWrite} onChange={(e) => setName(e.target.value)} />}</Field>
          <Field label={t('replyTemplates.folder')}>{(p) => <Input {...p} value={folder} disabled={!canWrite} onChange={(e) => setFolder(e.target.value)} />}</Field>
        </div>
        <MarkdownEditor aria-label={t('replyTemplates.body')} rows={12} value={body} readOnly={!canWrite} onChange={setBody} />
        <p className="flex flex-wrap items-center gap-1 text-xs text-fg-3">
          {t('replyTemplates.placeholders')}
          {placeholders.map((p) => (
            <Badge key={p} tone="outline">{`{{${p}}}`}</Badge>
          ))}
        </p>
        {error && (
          <p role="alert" className="text-sm text-danger">
            {error}
          </p>
        )}
        {canWrite && (
          <div className="flex flex-wrap justify-between gap-2">
            <ConfirmButton
              variant="danger"
              icon={<Trash2 />}
              label={t('common.delete')}
              title={t('replyTemplates.deleteTitle', { name: template.name })}
              onConfirm={() => remove.mutateAsync({ input: { id: template.id }, ifMatch: template.version })}
            />
            <Button
              disabled={!name.trim()}
              loading={save.isPending}
              onClick={() =>
                save.mutate({ input: { id: template.id, name: name.trim(), folder: folder.trim() || null, body }, ifMatch: template.version }, { onSuccess: () => setError(null), onError: (e) => setError(message(e)) })
              }
            >
              {t('common.save')}
            </Button>
          </div>
        )}
      </Card>
      <AttachmentsPanel type="supportTemplate" id={template.id} archived={!canWrite} />
    </div>
  );
}

/** Reply templates (CO-0622): per folder, with placeholders and files; the editor of a ticket inserts them. */
function TemplatesPage() {
  const { t } = useTranslation();
  const message = useErrorMessage();
  const s = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const canWrite = useCan('support:update');
  const q = useQuery(templatesQuery());
  const create = useActionMutation(createTemplate, {
    invalidate: [['support', 'template']],
    onSuccess: (r) => void navigate({ search: { id: r.id }, replace: true }),
  });
  if (q.isError) return <ErrorState title={t('errors.generic')} description={message(q.error)} />;
  if (!q.data) return <Skeleton className="h-96" />;
  const items = q.data.items;
  const selected = items.find((x) => x.id === s.id) ?? items[0];
  const groups = [...new Set(items.map((x) => x.folder ?? ''))];
  return (
    <>
      <PageHeader
        title={t('replyTemplates.title')}
        description={t('replyTemplates.description')}
        actions={
          canWrite && (
            <Button loading={create.isPending} onClick={() => create.mutate({ input: { name: t('replyTemplates.newName', { n: items.length + 1 }), body: '' } })}>
              <Plus /> {t('replyTemplates.new')}
            </Button>
          )
        }
      />
      {!items.length ? (
        <EmptyState title={t('replyTemplates.none')} description={canWrite ? t('replyTemplates.emptyHint') : undefined} />
      ) : (
        <div className="grid gap-6 lg:grid-cols-[16rem_minmax(0,1fr)]">
          <nav className="grid content-start gap-3" aria-label={t('replyTemplates.title')}>
            {groups.map((g) => (
              <div key={g || '_'} className="grid gap-1">
                <span className="text-xs font-bold uppercase text-fg-3">{g || t('replyTemplates.noFolder')}</span>
                {items
                  .filter((x) => (x.folder ?? '') === g)
                  .map((x) => (
                    <Button key={x.id} variant={x.id === selected?.id ? 'secondary' : 'ghost'} className="justify-start" aria-current={x.id === selected?.id} onClick={() => void navigate({ search: { id: x.id }, replace: true })}>
                      {x.name}
                    </Button>
                  ))}
              </div>
            ))}
          </nav>
          {selected && <TemplateEditor key={`${selected.id}-${selected.version}`} template={selected} placeholders={q.data.placeholders} canWrite={canWrite} />}
        </div>
      )}
    </>
  );
}
